import type { SaleQuoteResponse } from "@medicorepay/shared";
import { ArrowLeft, Check, Printer } from "lucide-react";

interface SaleSuccessModalProps {
  invoice: { id: string; invoiceNumber: string; quote: SaleQuoteResponse };
  onClose: () => void;
  onPrint: () => void;
}

export function SaleSuccessModal({
  invoice,
  onClose,
  onPrint,
}: SaleSuccessModalProps) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[110]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full m-4 overflow-hidden animate-scale-in">
        {/* Success Animation Header */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-8 pt-10 pb-8 text-center relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3" />

          <div className="relative">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-700/30">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600 stroke-[3]" />
              </div>
            </div>
            <h2 className="text-2xl font-extrabold text-white mb-1">
              Sale Complete!
            </h2>
            <p className="text-emerald-100 text-sm">
              Transaction processed successfully
            </p>
          </div>
        </div>

        {/* Invoice Info */}
        <div className="px-8 py-6">
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-center mb-6">
            <div className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">
              Invoice Number
            </div>
            <div className="text-xl font-extrabold text-gray-900 font-mono tracking-wide">
              #{invoice.invoiceNumber}
            </div>
            <div className="mt-2 text-sm font-bold text-emerald-700">
              ₹{(invoice.quote.payablePaise / 100).toFixed(2)} paid
            </div>
          </div>

          <div className="mb-5 max-h-32 space-y-1 overflow-y-auto text-xs text-gray-600">
            {invoice.quote.lines.map((line) => (
              <div
                key={line.lineId}
                className="flex items-center justify-between gap-3"
              >
                <span className="truncate">
                  {line.productName} × {Number(line.quantity)}
                </span>
                <span className="font-semibold tabular-nums">
                  ₹{(line.totalPaise / 100).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-2.5">
            <button
              onClick={onPrint}
              className="w-full py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-bold text-sm hover:from-gray-700 hover:to-gray-800 transition-all flex items-center justify-center gap-2 shadow-lg shadow-gray-200"
            >
              <Printer className="w-4 h-4" />
              Print Receipt
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              New Sale
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
